import React, { useEffect, useState } from "react";
import Sidebar from "../layout/Sidebar";
import { useDispatch, useSelector } from "react-redux";
import { fetchYourOrder, updateOrder } from "../../redux/features/placeOrderSlice";
import AddReview from "../../modal/ProductReviewModal";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  StepConnector,
  stepConnectorClasses,
} from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import "./Order.css";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Order = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch();
  const { orderData } = useSelector((state) => state.order);
  const [isCollapsed, setIsCollapsed] = useState({});
  const [isBillingCollapsed, setIsBillingCollapsed] = useState({});
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedReview, setSelectedReview] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchYourOrder());
  }, [dispatch]);

  useEffect(() => {
    if (orderData?.length > 0) {
      setActiveOrder(orderData[0].id);
    }
  }, [orderData]);

  const handleOpenReviewModal = (orderId, productId, review = null) => {
    setSelectedOrderId(orderId);
    setSelectedProductId(productId);
    setSelectedReview(review);
    setEditMode(!!review);
    setShowReviewModal(true);
  };

  const handleCloseReviewModal = () => {
    setShowReviewModal(false);
    setEditMode(false);
    setSelectedOrderId("");
    setSelectedProductId("");
    setSelectedReview(null);
    document.body.classList.remove("modal-open");
  };

  const toggleCollapse = (orderId) => {
    setIsCollapsed((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const toggleBillingCollapse = (orderId) => {
    setIsBillingCollapsed((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const handleAction = (id, status) => {
    setLoading(true);
    dispatch(updateOrder({ id, status }));
  };

  const selectedOrder = orderData?.find((order) => order.id === activeOrder);

  const statusMap = {
    0: 0, // Pending Payment
    1: 1, // Ordered
    2: 2, // On The Way
    3: 3, // Delivered
    4: 3, // Cancelled (end state)
    5: 3, // Returned (end state)
    6: 3, // Refunded (end state)
  };

  const steps = [
    "Pending Payment",
    "Ordered",
    "On The Way",
    "Delivered",
    "Cancelled",
    "Returned",
    "Refunded",
  ];
  const currentStep = selectedOrder ? statusMap[selectedOrder.status] || 0 : 0;

  const DottedConnector = styled(StepConnector)(({ theme }) => ({
    [`& .${stepConnectorClasses.line}`]: {
      borderTopStyle: "dotted",
      borderTopWidth: 2,
      borderColor: "#bbb",
    },
    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
      borderColor: "var(--secondary-light-color)",
    },
    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
      borderColor: "var(--secondary-light-color)",
    },
  }));

  return (
    <section className="pt-5 pb-5 bg-light min-vh-100">
      <div className="container">
        <div className="row">
          {/* Sidebar */}
          <div className="col-lg-3 mb-4">
            <Sidebar />
          </div>

          {/* Orders Section */}
          <div className="col-lg-9">
            <div className="card border-0 shadow-sm rounded-4 bg-white">
              <div className="mb-4 border-bottom pb-2 d-md-flex justify-content-between align-items-center px-4 py-2">
                <h4 className="fw-semibold text-dark mb-0">{`${t('My_Orders')}`}</h4>
                <p className="text-muted small mb-0">{`${t('Track_manage')}`}</p>
              </div>

              <div className="row">
                {/* Left Order Tabs */}
                <div className="col-xl-2">
                  <ul className="nav flex-column nav-pills rounded-3 shadow-sm p-2">
                    {orderData && orderData.length > 0 ? (
                      orderData.map((order) => (
                        <li
                          key={order.id}
                          className={`nav-item mb-2 rounded-3 overflow-hidden ${activeOrder === order.id ? "active-order" : ""
                            }`}
                        >
                          <button
                            className={`nav-link w-100 text-start ${activeOrder === order.id
                              ? "bg-primary text-white"
                              : "bg-light text-dark"
                              }`}
                            onClick={() => setActiveOrder(order.id)}
                          >
                            <strong>{order.order_no}</strong>
                            <br />
                            <small className="text-muted">
                              {new Date(order.created_date * 1000).toLocaleDateString("en-US", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </small>
                          </button>
                        </li>
                      ))
                    ) : (
                      <p className="text-muted small">{t("No_orders_available")}.</p>
                    )}
                  </ul>
                </div>

                {/* Right Selected Order */}
                <div className="col-xl-10">
                  {selectedOrder ? (
                    <div className="card shadow-sm border-0 rounded-4 p-4 mb-4">
                      {/* Order Header */}
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                        <h6 className="fw-semibold text-dark mb-2">
                          {`${t('Order_ID')}`}{" "}
                          <span className="text-primary">{selectedOrder.order_no}</span>
                        </h6>
                        <span
                          className={`badge px-3 py-2 fs-6 rounded-pill ${selectedOrder.status === 3
                            ? "bg-success"
                            : selectedOrder.status === 4
                              ? "bg-danger"
                              : "bg-warning text-dark"
                            }`}
                        >
                          {selectedOrder.status === 0
                            ? "Pending Payment"
                            : selectedOrder.status === 1
                              ? "Ordered"
                              : selectedOrder.status === 2
                                ? "On the Way"
                                : selectedOrder.status === 3
                                  ? "Delivered"
                                  : selectedOrder.status === 4
                                    ? "Cancelled"
                                    : selectedOrder.status === 5
                                      ? "Returned"
                                      : selectedOrder.status === 6
                                        ? "Refund"
                                        : "N/A"}
                        </span>
                      </div>

                      {/* Order Dates */}
                      <div className="mb-3 small text-muted">
                        <div>
                          <b>{`${t('Placed_on')}`}</b>{" "}
                          {new Date(selectedOrder.created_date * 1000).toLocaleString("en-US", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                        {selectedOrder.status < 3 && (
                          <div>
                            <b>{`${t('Expected_Delivery')}`}</b>{" "}
                            {selectedOrder.expected_delivery
                              ? new Date(selectedOrder.expected_delivery * 1000).toLocaleDateString()
                              : "N/A"}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="d-flex justify-content-end mb-3">
                        {selectedOrder.status < 3 ? (
                          <button
                            className="btn btn-danger btn-sm rounded-pill px-3"
                            onClick={() => handleAction(selectedOrder.id, 4)}
                          >
                            {`${t('Cancel_Order')}`}
                          </button>
                        ) : selectedOrder.status === 3 ? (
                          <button
                            className="btn btn-outline-danger btn-sm rounded-pill px-3"
                            onClick={() => handleAction(selectedOrder.id, 5)}
                          >
                            {`${t('Return_Order')}`}
                          </button>
                        ) : null}
                      </div>

                      {/* Progress Timeline */}
                      <div>
                        {selectedOrder && (
                          <Box sx={{ width: "100%", background: "#fff", borderRadius: 2 }}>
                            <Box className="d-flex justify-content-between align-items-center mb-3">
                              <Typography variant="h6" className="fw-semibold text-dark" sx={{ fontWeight: 600 }}>
                                {`${t('Order_Progress')}`}
                              </Typography>
                            </Box>

                            <div
                              style={{
                                overflowX: "auto",
                                WebkitOverflowScrolling: "touch",
                                scrollbarWidth: "thin", // for Firefox
                              }}
                              className="custom-stepper-scroll"
                            >
                              <Stepper
                                alternativeLabel
                                activeStep={currentStep}
                                connector={<DottedConnector />}
                                sx={{
                                  minWidth: "650px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  "& .MuiStepLabel-label": {
                                    fontSize: "0.9rem",
                                    color: "#555",
                                    whiteSpace: "nowrap",
                                  },
                                  "& .MuiStepIcon-root": { color: "#ccc" },
                                  "& .MuiStepIcon-root.Mui-active": {
                                    color: "var(--secondary-light-color)",
                                  },
                                  "& .MuiStepIcon-root.Mui-completed": {
                                    color: "var(--secondary-light-color)",
                                  },
                                }}
                              >
                                {steps.map((label, index) => (
                                  <Step key={index}>
                                    <StepLabel sx={{ mb: 2 }}>{label}</StepLabel>
                                  </Step>
                                ))}
                              </Stepper>
                            </div>
                          </Box>
                        )}
                      </div>

                      {/* Addresses */}
                      <div className="row mt-2 g-3">
                        {/* Shipping */}
                        <div className="col-md-6">
                          <div className="bg-light rounded-3 p-3 shadow-sm">
                            <h6 className="d-flex justify-content-between align-items-center">
                              {`${t('Shipping_Address')}`}
                              <button
                                className="btn btn-sm btn-link"
                                onClick={() => toggleCollapse(selectedOrder.id)}
                              >
                                <i
                                  className={`fas ${isCollapsed[selectedOrder.id]
                                    ? "fa-chevron-up"
                                    : "fa-chevron-down"
                                    }`}
                                ></i>
                              </button>
                            </h6>
                            {!isCollapsed[selectedOrder.id] && (
                              <div className="small text-muted">
                                <p className="mb-1">{selectedOrder.shipping_address?.address}</p>
                                <p className="mb-1">{selectedOrder.shipping_address?.house_no}</p>
                                <p className="mb-1">{selectedOrder.shipping_address?.city}</p>
                                <p>
                                  {selectedOrder.shipping_address?.pincode},{" "}
                                  {selectedOrder.shipping_address?.country}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Billing */}
                        <div className="col-md-6">
                          <div className="bg-light rounded-3 p-3 shadow-sm">
                            <h6 className="d-flex justify-content-between align-items-center">
                              {`${t('Billing_Address')}`}
                              <button
                                className="btn btn-sm btn-link"
                                onClick={() => toggleBillingCollapse(selectedOrder.id)}
                              >
                                <i
                                  className={`fas ${isBillingCollapsed[selectedOrder.id]
                                    ? "fa-chevron-up"
                                    : "fa-chevron-down"
                                    }`}
                                ></i>
                              </button>
                            </h6>
                            {!isBillingCollapsed[selectedOrder.id] && (
                              <div className="small text-muted">
                                <p className="mb-1">{selectedOrder.billing_address?.address}</p>
                                <p className="mb-1">{selectedOrder.billing_address?.house_no}</p>
                                <p className="mb-1">{selectedOrder.billing_address?.city}</p>
                                <p>
                                  {selectedOrder.billing_address?.pincode},{" "}
                                  {selectedOrder.billing_address?.country}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Ordered Items */}
                      <div className="mt-4">
                        <h6 className="fw-semibold mb-3">{`${t('Ordered_Items')}`}</h6>
                        {selectedOrder.items && selectedOrder.items.length > 0 ? (
                          selectedOrder.items.map((item, idx) => (
                            <div key={idx} className="card border-0 shadow-sm mb-3 p-3 rounded-3">
                              <div className="row align-items-center">
                                <div className="col-md-2 col-4">
                                  <Link to={`/product/${item?.product_id}`}>
                                    <img
                                      src={item.image || "https://royshop.in/assets/no-image.png"}
                                      alt="No Image"
                                      className="img-fluid rounded-3"
                                      onError={(e) => {
                                        e.target.onerror = null; // prevents infinite loop
                                        e.target.src = "https://amzio.de/api/uploads/products/no-thumbnail.png";
                                      }}
                                    />
                                  </Link>
                                </div>
                                <div className="col-md-6 col-8">
                                  <h6 className="mb-1">{item.title}</h6>
                                  <p className="small text-muted">Qty: {item.quantity}</p>
                                  {item.combinations?.map((combo, i) => (
                                    <small key={i} className="text-muted d-block">
                                      {combo.key}: {combo.value}
                                    </small>
                                  ))}
                                </div>
                                <div className="col-md-4 text-end">
                                  <h6 className="fw-semibold text-primary">€ {item.sales_price}</h6>
                                  <button
                                    className="btn btn-primary btn-sm rounded-pill mt-3 px-3"
                                    onClick={() =>
                                      handleOpenReviewModal(selectedOrder.id, item.product_id)
                                    }
                                    disabled={item.review_given}
                                  >
                                    Review Product
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted small">No items found.</p>
                        )}
                      </div>

                      {/* Order Summary */}
                      <div className="mt-4">
                        <h6 className="fw-semibold mb-3">{`${t('Order_Summary')}`}</h6>
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableBody>
                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                                  {t("Payment_Gateway")}
                                </TableCell>
                                <TableCell>
                                  {selectedOrder.gateway_type === 1
                                    ? "Paypal"
                                    : selectedOrder.gateway_type === 2
                                      ? "Stripe"
                                      : selectedOrder.gateway_type === 3
                                        ? "Klarna"
                                        : "N/A"}
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                                  {t("Payment_Status")}
                                </TableCell>
                                <TableCell>
                                  {selectedOrder.transaction_status === 0
                                    ? "Waiting"
                                    : selectedOrder.transaction_status === 1
                                      ? "Failed"
                                      : selectedOrder.transaction_status === 2
                                        ? "Paid"
                                        : "N/A"}
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                                  {t("Subtotal")}
                                </TableCell>
                                <TableCell>€ {selectedOrder.pay_amount}</TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                                  {t("Discount")}
                                </TableCell>
                                <TableCell>€ {selectedOrder.coupon_discount || "0"}</TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                                  {t("Shipping")}
                                </TableCell>
                                <TableCell>€ {selectedOrder.shipping_fee || "0"}</TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                                  {t("Total")}
                                </TableCell>
                                <TableCell>€ {selectedOrder.pay_amount}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>

                      </div>
                    </div>
                  ) : (
                    <p className="text-muted">{t("Select_an_order_to_view_details")}.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Review Modal */}
      <AddReview
        showModal={showReviewModal}
        handleCloseModal={handleCloseReviewModal}
        selectedOrderId={selectedOrderId}
        selectedProductId={selectedProductId}
        editMode={editMode}
        selectedReview={selectedReview}
      />
    </section>
  );
};

export default Order;
